import { Injectable } from '@angular/core';
import { FormGroup, FormArray } from '@angular/forms';
import { Subject, takeUntil, tap } from 'rxjs';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../models/form-group-type';

@Injectable({
    providedIn: 'root',
})
export class FormEntryService {
    /**
     * Checks if the form is a NestedFormGroupType.
     * @param form
     * @returns boolean
     */
    public isNestedFormGroup<T>(
        form: FormGroup<FormGroupType<T>> | FormGroup<NestedFormGroupType<T>>,
    ): form is FormGroup<NestedFormGroupType<T>> {
        return Object.values(form.controls).some(
            (control) => control instanceof FormArray,
        );
    }

    /**
     * Keeps track of whether the form is dirty. This is used to prevent page navigation when the form has not been saved
     * and the current action doesn't involve saving the form to the server.
     */
    public handleFormDirtying<T>(
        form: FormGroup<FormGroupType<T>> | FormGroup<NestedFormGroupType<T>>,
        destroy$: Subject<void>,
        formIsDirtyCallback: (value: boolean) => void,
    ) {
        if (this.isNestedFormGroup<T>(form)) {
            this.handleFormDirtyingHelper<T>(
                form as FormGroup<FormGroupType<T>>,
                destroy$,
                formIsDirtyCallback,
            );
        } else {
            this.handleFormDirtyingHelper<T>(
                form,
                destroy$,
                formIsDirtyCallback,
            );
        }
    }

    /**
     * Helper function to keep track of whether the form is dirty.
     * @param form
     */
    private handleFormDirtyingHelper<T>(
        form: FormGroup<FormGroupType<T>>,
        destroy$: Subject<void>,
        formIsDirtyCallback: (isFormDirty: boolean) => void,
    ) {
        form.valueChanges
            .pipe(
                takeUntil(destroy$),
                tap(() => {
                    formIsDirtyCallback(form.dirty);
                }),
            )
            .subscribe();
    }
}
